import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { MentionCandidate } from "@/types/mention";

/** Caches the @-mention picker pool per commentable object (issue 1465). Each object
 *  type has an idempotent `ensureFor…` (safe to call from an effect; fetches once
 *  per key) and a reactive `…Candidates` getter the composer reads. */
export class MentionableUsersStore {
  private byKey = observable.map<string, MentionCandidate[]>();
  private loadingKeys = observable.set<string>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  private get(key: string): MentionCandidate[] {
    return this.byKey.get(key) ?? [];
  }

  private ensure(key: string, fetcher: () => Promise<MentionCandidate[]>): void {
    if (this.byKey.has(key) || this.loadingKeys.has(key)) return;
    this.loadingKeys.add(key);
    fetcher()
      .then((users) => {
        runInAction(() => this.byKey.set(key, users));
      })
      .catch((error) => {
        // Non-critical: the composer just shows an empty picker. Surface enough
        // to diagnose (which pool failed) without breaking the comment box.
        console.warn("Failed to load mentionable users", { key, error });
      })
      .finally(() => {
        runInAction(() => this.loadingKeys.delete(key));
      });
  }

  private static projectKey = (slug: string, projectId: string) => `project:${slug}:${projectId}`;
  private static workItemKey = (slug: string, projectId: string, issueId: string) =>
    `work-item:${slug}:${projectId}:${issueId}`;
  private static workspaceWorkItemKey = (slug: string, issueId: string) =>
    `ws-work-item:${slug}:${issueId}`;
  private static submissionKey = (slug: string, submissionId: string) =>
    `submission:${slug}:${submissionId}`;

  ensureForProject(slug: string, projectId: string): void {
    this.ensure(MentionableUsersStore.projectKey(slug, projectId), () =>
      this.rootStore.mentionableUsersService.listForProject(slug, projectId),
    );
  }
  projectCandidates(slug: string, projectId: string): MentionCandidate[] {
    return this.get(MentionableUsersStore.projectKey(slug, projectId));
  }

  ensureForWorkItem(slug: string, projectId: string, issueId: string): void {
    this.ensure(MentionableUsersStore.workItemKey(slug, projectId, issueId), () =>
      this.rootStore.mentionableUsersService.listForWorkItem(slug, projectId, issueId),
    );
  }
  workItemCandidates(slug: string, projectId: string, issueId: string): MentionCandidate[] {
    return this.get(MentionableUsersStore.workItemKey(slug, projectId, issueId));
  }

  ensureForWorkspaceWorkItem(slug: string, issueId: string): void {
    this.ensure(MentionableUsersStore.workspaceWorkItemKey(slug, issueId), () =>
      this.rootStore.mentionableUsersService.listForWorkspaceWorkItem(slug, issueId),
    );
  }
  workspaceWorkItemCandidates(slug: string, issueId: string): MentionCandidate[] {
    return this.get(MentionableUsersStore.workspaceWorkItemKey(slug, issueId));
  }

  ensureForSubmission(slug: string, submissionId: string): void {
    this.ensure(MentionableUsersStore.submissionKey(slug, submissionId), () =>
      this.rootStore.mentionableUsersService.listForSubmission(slug, submissionId),
    );
  }
  submissionCandidates(slug: string, submissionId: string): MentionCandidate[] {
    return this.get(MentionableUsersStore.submissionKey(slug, submissionId));
  }
}
